import React, { useState } from 'react';
import { login, register } from '../services/api';
import './Login.css';

const MARKETS = [
  { id: 'sportsbook', label: 'Sportsbook', icon: '🏆' },
  { id: 'casino',     label: 'Casino',     icon: '🃏' },
];

// GeoComply operators selectable at login. Each routes the BrowserGuard SDK to a
// different regional cluster, so latency can be compared between them.
const OPERATORS = [
  { id: 'LatencyTestBR1',   label: 'LatencyTest BR1',   region: 'Brazil', flag: '🇧🇷' },
  { id: 'LatencyTestLatam', label: 'LatencyTest LATAM', region: 'Latin America', flag: '🌎' },
];

function Login({ onLoginSuccess }) {
  const [selectedMarket, setSelectedMarket] = useState('sportsbook');
  const [selectedOperator, setSelectedOperator] = useState(OPERATORS[0].id);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showSignup, setShowSignup] = useState(false);

  // Signup form state
  const [signupUsername, setSignupUsername] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupConfirmPassword, setSignupConfirmPassword] = useState('');
  const [signupError, setSignupError] = useState('');
  const [signupLoading, setSignupLoading] = useState(false);

  // Forgot password form state
  const [resetEmail, setResetEmail] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await login(username, password, null, selectedOperator);

      if (response.success) {
        onLoginSuccess(response.user, response.geoData, selectedMarket, selectedOperator);
      } else {
        setError(response.message || 'Login failed');
      }
    } catch (err) {
      console.error('Login error:', err);

      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('An error occurred during login. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setSignupError('');

    if (!signupUsername || !signupEmail || !signupPassword || !signupConfirmPassword) {
      setSignupError('All fields are required');
      return;
    }

    if (signupPassword !== signupConfirmPassword) {
      setSignupError('Passwords do not match');
      return;
    }

    if (signupPassword.length < 6) {
      setSignupError('Password must be at least 6 characters');
      return;
    }

    setSignupLoading(true);

    try {
      const data = await register(signupUsername, signupEmail, signupPassword);

      if (data.success) {
        setSignupUsername('');
        setSignupEmail('');
        setSignupPassword('');
        setSignupConfirmPassword('');
        setShowSignup(false);
        setUsername(signupUsername);

        if (data.emailPreviewUrl) {
          console.log('📧 Email Preview URL:', data.emailPreviewUrl);
        }

        alert('Registration successful! A welcome email with your login credentials has been sent to ' + signupEmail);
      } else {
        setSignupError(data.message || 'Registration failed');
      }
    } catch (err) {
      console.error('Signup error:', err);
      setSignupError('An error occurred during registration. Please try again.');
    } finally {
      setSignupLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetError('');
    setResetSuccess('');

    if (!resetEmail) {
      setResetError('Email is required');
      return;
    }

    setResetLoading(true);

    // Client-side demo: no real mail server. Always acknowledge (also the
    // security-best-practice behaviour — don't reveal whether an account exists).
    try {
      await new Promise((r) => setTimeout(r, 600));
      setResetSuccess('If an account exists with this email, a password reset link has been sent.');
      setResetEmail('');
      setTimeout(() => {
        setShowForgotPassword(false);
        setResetSuccess('');
      }, 3000);
    } catch (err) {
      setResetError('An error occurred. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-background">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
      </div>

      <div className="login-content">
        <div className="login-header">
          <div className="logo-container">
            <div className="logo-icon">
              <img src="/geocomply-logo.jpg" alt="GeoComply" style={{width:'48px',height:'48px',borderRadius:'8px',objectFit:'contain',background:'transparent',padding:'0'}} />
            </div>
            <div>
              <h1 className="logo">Latency Test<span className="logo-accent"> Demo</span></h1>
              <p className="tagline">Sportsbook &amp; Casino · Powered by GeoComply</p>
            </div>
          </div>
        </div>

        <div className="market-switcher">
          {MARKETS.map(m => (
            <button
              key={m.id}
              type="button"
              className={`market-tab${selectedMarket === m.id ? ' market-tab--active' : ''}`}
              onClick={() => setSelectedMarket(m.id)}
            >
              <span className="market-tab-icon">{m.icon}</span>
              <span className="market-tab-label">{m.label}</span>
            </button>
          ))}
        </div>

        <form className="login-form" onSubmit={handleSubmit}>
          <h2>Sign In to Your Account</h2>

          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          <div className="form-group">
            <label>GeoComply Operator</label>
            <div className="operator-switcher" role="radiogroup" aria-label="GeoComply operator">
              {OPERATORS.map(op => (
                <button
                  key={op.id}
                  type="button"
                  role="radio"
                  aria-checked={selectedOperator === op.id}
                  className={`operator-tab${selectedOperator === op.id ? ' operator-tab--active' : ''}`}
                  onClick={() => setSelectedOperator(op.id)}
                  disabled={loading}
                >
                  <span className="operator-tab-flag">{op.flag}</span>
                  <span className="operator-tab-text">
                    <span className="operator-tab-label">{op.label}</span>
                    <span className="operator-tab-region">{op.region}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="username">Username or Email</label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your username"
              required
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            className="login-button"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-small"></span>
                Signing In...
              </>
            ) : (
              'Sign In'
            )}
          </button>

          <div className="signup-link">
            Don't have an account? <button type="button" onClick={() => setShowSignup(true)} className="signup-btn">Sign up</button>
          </div>

          <div className="demo-accounts">
            <p className="demo-label">Test Accounts</p>
            <div className="demo-btns">
              {[1, 2, 3, 4, 5].map(n => (
                <button
                  key={n}
                  type="button"
                  className="demo-user-btn"
                  onClick={() => setUsername(`bg-demo-${n}`)}
                >
                  {`Test ${n}`}
                </button>
              ))}
            </div>
          </div>
        </form>

      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="modal-overlay" onClick={() => setShowForgotPassword(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowForgotPassword(false)}>×</button>
            <h2>Reset Your Password</h2>
            <p>Enter your email address and we'll send you a link to reset your password.</p>

            {resetError && (
              <div className="error-message" style={{ marginBottom: '16px' }}>
                <span className="error-icon">⚠️</span>
                {resetError}
              </div>
            )}

            {resetSuccess && (
              <div className="success-message" style={{ marginBottom: '16px', background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)', color: '#22c55e', padding: '12px', borderRadius: '6px' }}>
                <span style={{ marginRight: '8px' }}>✓</span>
                {resetSuccess}
              </div>
            )}

            <form onSubmit={handleForgotPassword}>
              <input
                type="email"
                placeholder="Email address"
                className="modal-input"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                disabled={resetLoading}
                required
              />
              <button
                type="submit"
                className="modal-button"
                disabled={resetLoading}
              >
                {resetLoading ? (
                  <>
                    <span className="spinner-small"></span>
                    Sending...
                  </>
                ) : (
                  'Send Reset Link'
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Sign Up Modal */}
      {showSignup && (
        <div className="modal-overlay" onClick={() => setShowSignup(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowSignup(false)}>×</button>
            <h2>Create Your Account</h2>
            <p>Join thousands of players winning big!</p>

            {signupError && (
              <div className="error-message" style={{ marginBottom: '16px' }}>
                <span className="error-icon">⚠️</span>
                {signupError}
              </div>
            )}

            <form onSubmit={handleSignup}>
              <input
                type="text"
                placeholder="Username"
                className="modal-input"
                value={signupUsername}
                onChange={(e) => setSignupUsername(e.target.value)}
                disabled={signupLoading}
                required
              />
              <input
                type="email"
                placeholder="Email address"
                className="modal-input"
                value={signupEmail}
                onChange={(e) => setSignupEmail(e.target.value)}
                disabled={signupLoading}
                required
              />
              <input
                type="password"
                placeholder="Password (min 6 characters)"
                className="modal-input"
                value={signupPassword}
                onChange={(e) => setSignupPassword(e.target.value)}
                disabled={signupLoading}
                required
              />
              <input
                type="password"
                placeholder="Confirm Password"
                className="modal-input"
                value={signupConfirmPassword}
                onChange={(e) => setSignupConfirmPassword(e.target.value)}
                disabled={signupLoading}
                required
              />
              <button
                type="submit"
                className="modal-button"
                disabled={signupLoading}
              >
                {signupLoading ? (
                  <>
                    <span className="spinner-small"></span>
                    Creating Account...
                  </>
                ) : (
                  'Create Account'
                )}
              </button>
            </form>
            <p className="modal-terms">By signing up, you agree to our Terms of Service and Privacy Policy.</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default Login;
